"use client";

import { useEffect, useState } from "react";
import { runExitImpersonation } from "@/lib/auth/impersonation-exit";
import { clearCart } from "@/lib/cart/storage";

// Step 11 commit 1 / DIRECTIVE-037 § Commit 1: persistent banner
// across the entire portal whenever an impersonation cookie is active.
// Server passes the session metadata (target name, started time) as
// props so the banner renders correctly on first paint - no FOUC.
// The "Exit" button calls POST /api/admin/impersonate/exit which
// clears the cookie + we then hard-navigate to /admin/clients.
//
// PP1 bug #2 fix (Jun 17): router.refresh() + router.push() race
// each other in the App Router's client cache - refresh was getting
// cancelled by the push, and the cached layout under /admin/clients
// kept rendering the (now-cookieless) ImpersonationBannerGate with
// the previous render's state. window.location.href is a full
// browser nav: clean cookie jar read + fresh server render + the
// banner component unmounts. Same pattern as the View-as start
// button on /admin/clients.

export type ImpersonationBannerProps = {
  targetClientName: string;
  adminEmail: string;
  startedAtIso: string;
  // Expiration epoch seconds - drives the live countdown so the
  // admin can see how much time remains before the cookie expires.
  expiresAtEpochSeconds: number;
};

export function ImpersonationBanner({
  targetClientName,
  adminEmail,
  startedAtIso,
  expiresAtEpochSeconds,
}: ImpersonationBannerProps) {
  const [now, setNow] = useState(() => Date.now());
  const [exiting, setExiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const startedMs = new Date(startedAtIso).getTime();
  const elapsedMs = now - startedMs;
  const remainingMs = expiresAtEpochSeconds * 1000 - now;

  async function handleExit() {
    setExiting(true);
    setError(null);
    // PASTE #363 (BUG C): drop the impersonated client's local cart on the way
    // out so it can never linger in the admin's own session. Boot hydrate also
    // reloads the admin's server cart, but clearing here is the explicit
    // guarantee before the hard nav.
    clearCart();
    await runExitImpersonation({
      fetchImpl: (input, init) => fetch(input, init),
      navigate: (href) => {
        window.location.href = href;
      },
      onError: (msg) => {
        setError(msg);
        setExiting(false);
      },
    });
  }

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="fixed left-0 right-0 top-0 z-[100] flex flex-wrap items-center justify-between gap-3 border-b border-red-700 bg-gradient-to-r from-red-600 to-orange-500 px-4 py-2.5 text-white shadow-md md:px-8"
    >
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span
          aria-hidden
          className="inline-flex h-2.5 w-2.5 animate-pulse rounded-full bg-white"
        />
        <span className="font-semibold uppercase tracking-[0.1em]">
          Impersonating
        </span>
        <span className="font-bold">{targetClientName}</span>
        <span className="hidden text-white/80 md:inline">
          · started {formatRelative(elapsedMs)} ago
        </span>
        <span className="text-white/80">· {formatCountdown(remainingMs)} left</span>
        <span className="hidden text-[11px] text-white/70 lg:inline">
          · admin: {adminEmail}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {error && (
          <span
            role="alert"
            data-testid="impersonation-exit-error"
            className="rounded bg-white px-3 py-1.5 text-xs font-semibold text-red-700"
          >
            {error}
          </span>
        )}
        <button
          type="button"
          onClick={handleExit}
          disabled={exiting}
          data-testid="impersonation-exit"
          className="rounded-full bg-white px-5 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60"
        >
          {exiting ? "Exiting..." : "Exit"}
        </button>
      </div>
    </div>
  );
}

function formatRelative(ms: number): string {
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
